import { MailOutlineOutlined } from '@mui/icons-material';
import { Chip, Tooltip } from '@mui/material';
import { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import {
    RESENDABLE_STATUSES,
    STATUS_COLOURS,
    STATUS_LABELS,
} from '../../library/membershipDisplay';
import { MembershipStatus } from '../../library/models/clinic';
import ResourcesIds from '../../resources/ResourcesIds';

interface IMembershipStatusChipProps {
    status: MembershipStatus;
    /**
     * What to do when the chip is used to send the invitation again. Omitted where the
     * status is only being reported -- the chip stays inert rather than looking actionable
     * somewhere nothing can act on it.
     */
    onResend?: () => void;
}

/**
 * A membership status, and -- where the status is one that a fresh invitation would fix --
 * the way to send that invitation.
 *
 * The action lives on the chip because the chip is the reason to take it: a therapist
 * reading "Invitation expired" is already looking at the thing they want to undo. The cost
 * of that is discoverability, since a badge reads as output rather than as a control, so
 * the actionable ones are given a mail icon, a pointer and a tooltip. Without those it is
 * a label that happens to be clickable, which nobody would ever click.
 *
 * Only two of the four statuses are actionable, so the affordance has to be visible on
 * the chip itself rather than assumed of chips in general.
 */
export const MembershipStatusChip = ({
    status,
    onResend,
}: IMembershipStatusChipProps): JSX.Element => {
    const { t: translate } = useTranslation();

    const label = translate(STATUS_LABELS[status]);
    const canResend = onResend !== undefined && RESENDABLE_STATUSES.includes(status);

    const chip = (
        <Chip
            size="small"
            color={STATUS_COLOURS[status]}
            label={label}
            icon={canResend ? <MailOutlineOutlined /> : undefined}
            clickable={canResend}
            onClick={canResend ? onResend : undefined}
            /**
             * The visible text says what is wrong; the accessible name has to say what the
             * control does, and still contain the visible text so that speaking the label
             * aloud works as a voice command.
             */
            aria-label={
                canResend
                    ? translate(ResourcesIds.MEMBER_STATUS_RESEND_LABEL, { status: label })
                    : undefined
            }
        />
    );

    if (!canResend) {
        return chip;
    }

    return (
        <Tooltip title={translate(ResourcesIds.PATIENTS_RESEND_INVITATION_ACTION)}>
            {chip}
        </Tooltip>
    );
};
